import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  getPerfectYearAgenda,
  getPerfectYearRuns,
  upsertPerfectYearRun,
} from '../api/client.js';

// PerfectYearAgenda — Steve's quarterly business agenda. Year-map view
// (categories × Q1-Q4) on top; click a quarter cell on an item to focus the
// detail panel where you mark it complete and capture outcome notes.

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

function currentYear() {
  return new Date().getFullYear();
}
function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}
function fullQuarterCode(year, qNum) {
  return `${year}-Q${qNum}`;
}
function itemTargetsQuarter(item, qNum) {
  return (item.target_quarters || '')
    .split(',')
    .map((s) => s.trim())
    .includes(`Q${qNum}`);
}

export default function PerfectYearAgenda() {
  const [year, setYear] = useState(currentYear());
  const [agenda, setAgenda] = useState({ categories: [], items: [] });
  const [runs, setRuns] = useState([]); // runs across all four quarters of `year`
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null); // { item, qNum }

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const a = await getPerfectYearAgenda();
      const allRuns = (
        await Promise.all(QUARTERS.map((_, i) => getPerfectYearRuns(fullQuarterCode(year, i + 1))))
      ).flat();
      setAgenda(a);
      setRuns(allRuns);
    } catch (err) {
      setLoadError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    reload();
  }, [reload]);

  const runByKey = useMemo(() => {
    const m = new Map();
    for (const r of runs) m.set(`${r.item_id}:${r.quarter}`, r);
    return m;
  }, [runs]);

  const itemsByCategory = useMemo(() => {
    const m = new Map();
    for (const it of agenda.items) {
      if (!m.has(it.category_id)) m.set(it.category_id, []);
      m.get(it.category_id).push(it);
    }
    return m;
  }, [agenda.items]);

  async function setRunState({ item, qNum, completed, outcomeNotes }) {
    const quarter = fullQuarterCode(year, qNum);
    const saved = await upsertPerfectYearRun({
      itemId: item.id,
      quarter,
      completed,
      outcomeNotes,
    });
    if (!saved) return;
    setRuns((prev) => {
      const key = `${saved.item_id}:${saved.quarter}`;
      const idx = prev.findIndex((r) => `${r.item_id}:${r.quarter}` === key);
      if (idx === -1) return [...prev, saved];
      const next = prev.slice();
      next[idx] = saved;
      return next;
    });
    // Keep the focused item in sync.
    if (focus && focus.item.id === item.id && focus.qNum === qNum) {
      setFocus({ ...focus });
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading Perfect Year agenda…</p>;
  }
  if (loadError) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-700">
        Failed to load: {loadError}{' '}
        <button onClick={reload} className="underline">retry</button>
      </div>
    );
  }

  const thisYear = currentYear();
  const thisQ = currentQuarter();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow p-6 space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Perfect Year — {year}</h2>
            <p className="text-sm text-slate-500">
              Steve's quarterly business review agenda. Tick items as you complete them and capture
              the outcome.
            </p>
          </div>
          <YearSwitcher year={year} setYear={setYear} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase px-4 py-2 w-72">
                Category / Item
              </th>
              {QUARTERS.map((q, i) => {
                const qNum = i + 1;
                const isCurrent = year === thisYear && qNum === thisQ;
                return (
                  <th
                    key={q}
                    className={`text-center text-xs font-semibold uppercase px-3 py-2 ${
                      isCurrent ? 'text-emerald-700 bg-emerald-50' : 'text-slate-500'
                    }`}
                  >
                    {q}
                    {isCurrent ? <span className="ml-1 text-[10px]">(now)</span> : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {agenda.categories.map((cat) => {
              const items = itemsByCategory.get(cat.id) || [];
              if (!items.length) return null;
              return (
                <Fragment key={cat.id}>
                  <tr className="bg-slate-100/60">
                    <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {cat.name}
                    </td>
                  </tr>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2 text-slate-800">{it.title}</td>
                      {QUARTERS.map((_, i) => {
                        const qNum = i + 1;
                        const targeted = itemTargetsQuarter(it, qNum);
                        const run = runByKey.get(`${it.id}:${fullQuarterCode(year, qNum)}`);
                        return (
                          <td key={qNum} className="text-center px-3 py-2">
                            <QuarterCell
                              targeted={targeted}
                              run={run}
                              onClick={() => {
                                if (!targeted && !run) return;
                                setFocus({ item: it, qNum });
                              }}
                              focused={focus && focus.item.id === it.id && focus.qNum === qNum}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {focus ? (
        <FocusPanel
          year={year}
          focus={focus}
          run={runByKey.get(`${focus.item.id}:${fullQuarterCode(year, focus.qNum)}`)}
          onClose={() => setFocus(null)}
          onSave={setRunState}
        />
      ) : (
        <QuarterSummary
          year={year}
          quarter={thisQ}
          items={agenda.items}
          runByKey={runByKey}
          onFocus={(item, qNum) => setFocus({ item, qNum })}
        />
      )}
    </div>
  );
}

function YearSwitcher({ year, setYear }) {
  return (
    <div className="inline-flex items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => setYear(year - 1)}
        className="px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100"
      >
        ←
      </button>
      <span className="px-2 font-medium text-slate-800">{year}</span>
      <button
        type="button"
        onClick={() => setYear(year + 1)}
        className="px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100"
      >
        →
      </button>
    </div>
  );
}

function QuarterCell({ targeted, run, onClick, focused }) {
  if (!targeted && !run) {
    return <span className="text-slate-200">·</span>;
  }
  const done = !!run?.completed_at;
  const hasNotes = !!run?.outcome_notes;
  const cls = focused
    ? 'ring-2 ring-slate-900 ring-offset-1'
    : done
    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
    : 'bg-slate-100 text-slate-600 hover:bg-slate-200';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition ${cls}`}
      title={done ? `Done${hasNotes ? ' · has notes' : ''}` : hasNotes ? 'In progress · has notes' : 'Due'}
    >
      {done ? '✓' : hasNotes ? '•' : '○'}
    </button>
  );
}

function FocusPanel({ year, focus, run, onClose, onSave }) {
  const { item, qNum } = focus;
  const [notes, setNotes] = useState(run?.outcome_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setNotes(run?.outcome_notes || '');
    setError(null);
  }, [run?.id, item.id, qNum]);

  async function saveNotes() {
    if ((run?.outcome_notes || '') === notes) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ item, qNum, outcomeNotes: notes });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  async function toggleDone() {
    setSaving(true);
    setError(null);
    try {
      await onSave({ item, qNum, completed: !run?.completed_at });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  const isDone = !!run?.completed_at;

  return (
    <div className="bg-white rounded-2xl shadow p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {year}-Q{qNum} focus
          </div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">{item.title}</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-slate-400 hover:text-slate-700"
        >
          Close
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleDone}
          disabled={saving}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
            isDone
              ? 'bg-emerald-600 text-white hover:bg-emerald-500'
              : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
          } disabled:opacity-50`}
        >
          {isDone ? '✓ Done' : 'Mark done'}
        </button>
        {run?.completed_at ? (
          <span className="text-xs text-slate-500">
            Completed {new Date(run.completed_at).toLocaleDateString()}
          </span>
        ) : null}
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-slate-500">Outcome notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          rows={4}
          placeholder="What did you decide / change / commit to?"
          className="input mt-1 resize-y leading-relaxed"
        />
        <div className="text-xs mt-1">
          {saving ? <span className="text-slate-500">Saving…</span> : error ? <span className="text-rose-600">{error}</span> : <span className="text-slate-400">Saves on blur.</span>}
        </div>
      </div>
    </div>
  );
}

function QuarterSummary({ year, quarter, items, runByKey, onFocus }) {
  const dueItems = items.filter((it) => itemTargetsQuarter(it, quarter));
  const code = fullQuarterCode(year, quarter);
  const done = dueItems.filter((it) => runByKey.get(`${it.id}:${code}`)?.completed_at).length;

  return (
    <div className="bg-white rounded-2xl shadow p-6 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          This quarter — {code}
        </h3>
        <span className="text-xs text-slate-500">{done}/{dueItems.length} complete</span>
      </div>
      {dueItems.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing scheduled for {code}.</p>
      ) : (
        <ul className="space-y-1.5">
          {dueItems.map((it) => {
            const run = runByKey.get(`${it.id}:${code}`);
            const isDone = !!run?.completed_at;
            return (
              <li
                key={it.id}
                onClick={() => onFocus(it, quarter)}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded-md"
              >
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold ${
                    isDone ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {isDone ? '✓' : '○'}
                </span>
                <span className={isDone ? 'line-through text-slate-400' : 'text-slate-800'}>
                  {it.title}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
